import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import type { ReactNode } from 'react'

const domain = import.meta.env.VITE_AUTH0_DOMAIN
const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID
const audience = import.meta.env.VITE_AUTH0_AUDIENCE

if (!domain || !clientId) {
  throw new Error('Missing Auth0 environment variables')
}

interface AuthProviderProps {
  children: ReactNode
}

/**
 * NOTA: SupabaseAuthSync fue removido porque ahora usamos SupabaseProvider
 * con custom fetch interceptor que maneja la autenticación automáticamente.
 *
 * Ver: src/lib/supabase-context.tsx
 */

export function AuthProvider({ children }: AuthProviderProps) {
  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        ui_locales: 'es',
        ...(audience && { audience }),
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      // Auth0 vuelve siempre a `redirect_uri`, que es la raíz.
      // Un `code` y un `state` en otra ruta son la vuelta de otro OAuth, como el de Instagram, y Auth0 no los procesa.
      skipRedirectCallback={window.location.pathname !== '/'}
      // Callback para manejar la navegación después del login (redirección)
      onRedirectCallback={(appState) => {
        window.history.replaceState(
          {},
          document.title,
          appState?.returnTo || window.location.pathname
        )
      }}
    >
      {children}
    </Auth0Provider>
  )
}

// Re-export the hook for convenience
export { useAuth0 }

// Alias for compatibility
export const useAuth = useAuth0

/**
 * DEPRECADO: useSupabaseWithAuth fue removido.
 * Usar useSupabase() de '@/lib/supabase-context' en su lugar.
 *
 * @deprecated Use useSupabase() from '@/lib/supabase-context'
 */

/**
 * Hook para obtener el user ID del usuario autenticado en Auth0
 *
 * Con Third-Party Auth, el user ID proviene de Auth0, no de Supabase Auth.
 * Este hook extrae el 'sub' claim del token de Auth0.
 *
 * @returns user ID de Auth0 o null si no está autenticado
 */
export function useAuth0UserId(): string | null {
  const { user, isAuthenticated } = useAuth0()

  if (!isAuthenticated || !user) return null

  // El 'sub' claim contiene el user ID en formato: "auth0|<id>"
  return user.sub || null
}
